import React, {useCallback, useContext, useRef} from 'react';
import Dropdown from '@bonprix/pattern-library/templates/dropdown/default';
import RadioInput from '@bonprix/pattern-library/templates/input/Radio.jsx';
import Landmark from '@core/scripts/components/landmark-adapter';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import Messages from '@core/scripts/components/messages';
import InputGroup from '@bonprix/pattern-library/templates/input/Group.jsx';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import NitroEventHelper from '@core/scripts/helper/nitro-event-helper';
import {Constants} from '../view-constants';
import {useDispatch} from 'react-redux';
import {getContentAjaxUrl, yieldToMain} from '../ArticleOverviewHelper';
import {loadOn} from '../LoaderSlice.jsx';
import {setContentAjaxCall} from '../AjaxCallSlice.jsx';
import {TertiaryButton} from '@bonprix/design-system';
import {useTrinity} from '../hooks/use-trinity.js';
import {useBreakpointUp} from "bonprix-design-system";

export default function Ordering({ordering, isSticky}) {
    const {texts, contentAjaxUrl, prettyUrl, currentSearchParams} = useContext(articleOverviewStaticContext);
    const breakpointLTUp = useBreakpointUp('l');
    const dispatch = useDispatch();
    const {sendClickBasic} = useTrinity();

    // helper refs for Trinity Event to detect if ordering was opened without changing the value
    const isOpened = useRef(false);

    const classes = breakpointLTUp
        ? 'article-overview__ordering-wrapper'
        : 'article-overview__filter-order-button-wrapper -ordering';

    const handleOrderingChange = useCallback(
        (event, value = null, lm = null) => {
            // capture select values before yieldToMain to ensure consistency in async operations
            const select = event.currentTarget;
            const sortValue = value || select.value;
            const landmarkValue = lm || select.options[select.selectedIndex].getAttribute('jsh-option-landmark');

            yieldToMain(() => dispatch(loadOn()));
            yieldToMain(() => {
                reloadArticles(sortValue);

                if (breakpointLTUp) {
                    MessageBroker.publish(Messages.DROPDOWN.CLOSE);
                }

                Landmark.sendLandmark({
                    landmark: 'userinterface',
                    click: landmarkValue,
                    fixation: isSticky ? Constants.LANDMARK_STICKY : Constants.LANDMARK_NON_STICKY,
                });
                NitroEventHelper.sendTracedEvent('SORT_PRODUCT_LIST');
            });
        },
        // TODO: check if its nessesary to add the searchParams to the dependency array
        [isSticky, breakpointLTUp],
    );

    const reloadArticles = (sortOption) => {
        const currentSearchParamsCopy = new URLSearchParams(currentSearchParams.current);
        currentSearchParamsCopy.set(Constants.SEARCH_PARAMS_ORDERING, sortOption);
        currentSearchParamsCopy.delete(Constants.SEARCH_PARAMS_PAGE);
        currentSearchParamsCopy.delete(Constants.SEARCH_PARAMS_ANCHOR);

        const formattedParams = `?${currentSearchParamsCopy.toString()}`;
        const contentAjaxCall = getContentAjaxUrl(prettyUrl.current, formattedParams, contentAjaxUrl);

        currentSearchParams.current = currentSearchParamsCopy;
        dispatch(setContentAjaxCall([contentAjaxCall, false]));
    };

    return (
        <div className={classes} test-ref__ordering-wrapper="">
            {breakpointLTUp ? renderOrderingLarge() : renderOrderingSmall()}
        </div>
    );

    function renderOrderingLarge() {
        return (
            <Dropdown
                onLabelClick={(aboutToBeOpened) => {
                    if (aboutToBeOpened) {
                        sendClickBasic('open_sorting', Constants.FILTER_VIEW_EVENT_VERSION_CONTROLGROUP);
                    }
                }}
                dropdownItemClass="ordering"
                dropdownName={texts.sort.toLowerCase()}
                visibleLabel={texts.sort}
                customJsh="article-overview-ordering-selection"
            >
                <div className="ordering-list" test-ref__ordering-list="">
                    {ordering.map((option) => {
                        return renderOrderingOptionLarge(option);
                    })}
                </div>
            </Dropdown>
        );
    }

    function renderOrderingOptionLarge(option) {
        const handleOnChange = (event) => handleOrderingChange(event, option.value, option.landmark);

        return (
            <div className="ordering__option" test-ref__ordering-option={option.value} key={option.value}>
                <InputGroup className="ordering__radio-button">
                    <RadioInput
                        name="orderings"
                        value={option.value}
                        disabled={false}
                        checked={option.selected}
                        onChange={handleOnChange}
                    />
                    <div className="input-group__text">{option.name}</div>
                </InputGroup>
            </div>
        );
    }

    function renderOrderingSmall() {
        const selectedValue = ordering.find((option) => option.selected).value;
        const handlePointerDown = () => {
            if (isOpened.current) {
                isOpened.current = false;
            } else {
                sendClickBasic('open_sorting', Constants.FILTER_VIEW_EVENT_VERSION_CONTROLGROUP);
                isOpened.current = true;
            }
        };
        const handleChange = (e) => {
            handleOrderingChange(e);
            isOpened.current = false;
        };
        const handleBlur = () => {
            isOpened.current = false;
        };

        return (
            <>
                <div className="article-overview__filter-order-button">
                    <TertiaryButton Icon-Position="start" icon="sort" onClick={() => {}} small={true}>
                        {texts.sort}
                    </TertiaryButton>
                </div>
                <div
                    className="article-overview__ordering-button-input-wrapper"
                    test-ref__ordering-button-input-wrapper=""
                >
                    <select
                        className="article-overview__ordering-button-input"
                        onChange={handleChange}
                        value={selectedValue}
                        onPointerDown={handlePointerDown}
                        onBlur={handleBlur}
                    >
                        {ordering.map((option) => (
                            <option
                                key={option.value}
                                className="ordering__option"
                                value={option.value}
                                jsh-option-landmark={option.landmark}
                            >
                                {option.name}
                            </option>
                        ))}
                    </select>
                </div>
            </>
        );
    }
}
